<script lang="ts" setup>
import type { Ref } from 'vue'
import { schemaApiCreateToken, schemaApiGetStatus } from '../libs/apiSchema'
import type { FormState, RefToken } from '../types/forms'
import { Button, ModalLoader } from 'refresh-ui'
import type { Validation } from '@vuelidate/core'
import { flagError } from 'error-handling'
import SimpleSpinner from './SimpleSpinner.vue'

let tokenStart: Boolean = false
let deviceIsMobile: Boolean = true
const message = ref<string>('')
const more = ref<string>('')
const formState: Ref<FormState> = ref('entry')
const tokenId: Ref<RefToken> = ref('')
const QRCode: Ref<string> = ref('')
const statusCode: Ref<String> = ref('idle')

export interface BankIdAuth {
  bankId: boolean
  ssn: string
  name: { first: string; last: string }
  referenceToken?: string
}
export interface BankIdSignature {
  signature: string
  ocspResponse: string
  ip: string
}
interface BankIdData {
  Status: string
  ReferenceToken: string
  SSN?: string
  AutoStartToken: string
  QR: string | null
  Signature: null | string
  OcspResponse?: null | string
  IP?: null | string
  Created: string | null
}
const data = ref<BankIdData>({
  Status: '',
  ReferenceToken: '',
  SSN: '',
  AutoStartToken: '',
  QR: '',
  Signature: null as null | string,
  OcspResponse: null as null | string,
  Created: '',
})

const props = defineProps<{
  typeIsId: boolean
  sum: Number
  ssn?: string
  v$?: Ref<Validation>
}>()

const emit = defineEmits(['update:ssn', 'update:auth', 'update:name', 'update:signature', 'onNext'])

// Used for the countdown timer, default is 5 minutes
const countdownSla = ref({
  time: 5,
  expired: false,
})
const updateCountdown = ref(0)
provide('sla', countdownSla)
provide('updateCountdown', updateCountdown)

// Register API endpoint
const billectaApiRoute = (type: boolean) => {
  let route
  if (!type) {
    route = '/api/billecta/sign'
  } else {
    route = '/api/billecta-id'
  }
  return route
}
const apiRoute = billectaApiRoute(props.typeIsId)

// Reset values on retry
const retry = () => {
  tokenStart = false
  deviceIsMobile = true
  message.value = ''
  more.value = ''
  formState.value = 'entry'
  tokenId.value = ''
  QRCode.value = ''
  statusCode.value = 'idle'
  data.value = {
    Status: '',
    ReferenceToken: '',
    SSN: '',
    AutoStartToken: '',
    QR: '',
    Signature: null as null | string,
    OcspResponse: null as null | string,
    IP: null as null | string,
    Created: '',
  }
}

const openBankIdApp = (token: String) => {
  // Only use href for mobile devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
  const isAndroid = /android/i.test(userAgent)
  const isMobile = isIOS || isAndroid
  let bankIdUrl = ''

  if (isMobile) {
    // Use custom scheme for mobile devices
    bankIdUrl = `bankid:///?autostarttoken=${token}&redirect=null`
    window.location.href = bankIdUrl
  } else {
    // Desktop: use universal link
    bankIdUrl = `https://app.bankid.com/?autostarttoken=${token}&redirect=null`
    window.open(bankIdUrl, '_blank')
  }
}

const handleError = (errorMessage: string) => {
  statusCode.value = 'Error'
  formState.value = 'error'
  message.value = errorMessage
  QRCode.value = ''
  flagError(errorMessage, 'open bankid app - /form-bankid')
}

const handleSuccess = (statusRes: any, referenceToken: RefToken) => {
  formState.value = 'entry'
  statusCode.value = 'Complete'
  QRCode.value = ''
  // TODO: Set messages per interaction type
  message.value = props.typeIsId ? 'Identifiering klar' : 'Tack! Nu är månadsgåvan godkänd.'

  // Emit data
  if (statusRes?.SSN) emit('update:ssn', statusRes.SSN)
  if (statusRes?.GivenName || statusRes?.Surname) {
    emit('update:name', {
      first: statusRes.GivenName || '',
      last: statusRes.Surname || '',
    })
  }
  emit('update:auth', {
    bankId: true,
    ssn: statusRes?.SSN,
    name: {
      first: statusRes?.GivenName || '',
      last: statusRes?.Surname || '',
    },
    referenceToken: String(referenceToken),
  } as BankIdAuth)

  if (!props.typeIsId && statusRes?.Signature) {
    emit('update:signature', {
      signature: statusRes.Signature || '',
      ocspResponse: statusRes.OcspResponse || '',
    })
  }
}

/* API Calls */

// Initiate authentication/signature
const createToken = async () => {
  formState.value = 'processing'
  try {
    if (props.typeIsId) {
      data.value = schemaApiCreateToken.parse(
        await $fetch(apiRoute, {
          method: 'post',
          body: JSON.stringify({
            SSN: props.ssn,
            TokenStartRequired: tokenStart,
          }),
        }),
      ).response
    } else if (!props.typeIsId) {
      data.value = schemaApiCreateToken.parse(
        await $fetch(apiRoute, {
          method: 'post',
          body: JSON.stringify({
            SSN: props.ssn,
            UserNonVisibleData: 'hidden',
            // TODO: Placeholder message - Change later to what should be displayed in the BankID app
            UserMessage:
              'Jag godkänner en gåva till Djurens Rätt varje månad som betalas via autogiro. Detta kan när som helst avbrytas genom att kontakta Djurens Rätt.',
            TokenStartRequired: false, // tokenStart,
          }),
        }),
      ).response
    }

    const { Status, ReferenceToken, AutoStartToken, QR } = data.value
    statusCode.value = Status
    tokenId.value = ReferenceToken

    if (deviceIsMobile && AutoStartToken) {
      openBankIdApp(AutoStartToken)
    } else if (!deviceIsMobile && typeof QR === 'string') {
      QRCode.value = QR
      message.value = props.typeIsId
        ? 'Öppna BankID på din mobil och skanna QR-koden för att identifiera dig'
        : 'Öppna BankID på din mobil och skanna QR-koden'
      more.value = props.typeIsId ? '' : 'I appen ser du villkoren och kan godkänna dem.'
    }

    pollGetStatus(tokenId.value)
  } catch (error) {
    formState.value = 'error'
  }
}

const refreshToken = async () => {
  try {
    return schemaApiCreateToken.parse(
      await $fetch(apiRoute, {
        method: 'post',
        body: JSON.stringify({
          SSN: data.value.SSN,
          UserNonVisibleData: 'hidden',
          // TODO: Placeholder message - Change later to what should be displayed in the BankID app
          UserMessage:
            'Jag godkänner en gåva till Djurens Rätt varje månad som betalas via autogiro. Detta kan när som helst avbrytas genom att kontakta Djurens Rätt.',
          TokenStartRequired: false, // tokenStart,
        }),
      }),
    ).response
  } catch (e) {
    console.log('Error: ', e)
  }
}

// Check status of authentication/signature
const getStatus = async (id: RefToken) => {
  try {
    const response = schemaApiGetStatus.parse(await $fetch(`${apiRoute}?token=${id}`))
    // formState.value = 'entry'
    return { response }
  } catch (error) {
    console.log('Error: ', error)
    formState.value = 'error'
  }
}

const quitPolling = (currentTime: number, lastTokenRefresh: number): boolean => {
  return countdownSla.value.expired
}

let pollTimeout: ReturnType<typeof setTimeout> | null = null
const clearPollTimeout = () => {
  if (pollTimeout) {
    clearTimeout(pollTimeout)
    pollTimeout = null
  }
}

// Check status every second
const pollGetStatus = async (pollTokenId: RefToken) => {
  try {
    let token = pollTokenId
    let lastTokenRefresh = Date.now()
    let statusRes = (await getStatus(token))?.response?.response
    let status = statusRes?.Status

    const poll = async () => {
      statusRes = (await getStatus(token))?.response?.response
      QRCode.value = statusRes?.QR || ''
      status = statusRes?.Status
      statusCode.value = 'Started'
      const currentTime = Date.now()

      if (quitPolling(currentTime, lastTokenRefresh)) {
        handleError('Något gick fel, försök igen')
        clearPollTimeout()
        return
      }

      if (status === 'Complete') {
        handleSuccess(statusRes, token)
        clearPollTimeout()
        emit('onNext')
        return
      } else if (status === 'Error') {
        handleError('Något gick fel, försök igen')
        clearPollTimeout()
        return
      }

      // Check if it has been at least 20 seconds since the last token refresh
      if (currentTime - lastTokenRefresh >= 20 * 1000) {
        const newData = await refreshToken()

        if (!newData?.ReferenceToken) {
          handleError('Något gick fel, försök igen')
          clearPollTimeout()
          return
        }

        token = newData.ReferenceToken
        lastTokenRefresh = currentTime
      }
      pollTimeout = setTimeout(poll, 1000)
    }
    pollTimeout = setTimeout(poll, 1000)
  } catch (error) {
    handleError('Något gick fel, försök igen')
    clearPollTimeout()
  }
}

// Form submission
const handleForm = (device: boolean) => {
  deviceIsMobile = device
  tokenStart = true
  createToken()
}

onBeforeUnmount(() => {
  clearPollTimeout()
})
</script>

<template>
  <div class="mt-4">
    <div class="xl:max-w-[650px]">
      <!-- BankId internal spinner -->
      <ModalLoader
        v-if="
          (!deviceIsMobile && formState === 'processing') ||
          (!deviceIsMobile && formState === 'redirecting')
        "
        :qr="QRCode"
        :message="message"
        :more="more"
        :show-loader="false"
        :class="''"
      />

      <!-- User input -->
      <section v-if="statusCode !== 'Error'">
        <form>
          <div class="flex flex-col gap-4 md:items-center lg:items-start">
            <Button
              :title="'BankID på den här enheten'"
              :bank-id="true"
              color="blue"
              class="w-full md:max-w-[349px] lg:hidden"
              @click="handleForm(true)"
            />
            <Button
              :title="'Mobilt BankID'"
              :bank-id="true"
              color="blue"
              class="invisible -mt-16 w-full md:visible md:mt-0 md:max-w-[349px]"
              @click="handleForm(false)"
            />
          </div>
        </form>
        <SimpleSpinner v-if="statusCode === 'Complete'" form-state="processing" />
      </section>

      <!-- Error - retry? -->
      <section v-if="statusCode === 'Error'">
        <p>{{ message }}</p>
        <Button :title="'Försök igen'" color="blue" class="w-full lg:w-96" @click="retry" />
      </section>
    </div>
  </div>
</template>
